import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Eye,
  CheckCircle2,
  PartyPopper,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../../components/ui/sonner";

const empty = {
  title: "",
  excerpt: "",
  content: "",
  image: "",
  images: [],
  video_url: "",
  meta_title: "",
  meta_description: "",
  slug: "",
  status: "draft",
};

export default function AdminBlogEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === "nytt";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Inline celebration banner shown briefly after a successful save/publish.
  // Toasts are nice but easy to miss — this gives an inline confirmation
  // that stays put long enough to register.
  const [celebration, setCelebration] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/admin/blog/${id}`)
      .then((r) =>
        setForm({ ...empty, ...r.data, images: r.data.images || [] }),
      )
      .catch(() => toast.error("Kunde inte hämta inlägget"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const setField = (key) => (e) => {
    const value = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: r.data.url }));
      toast.success("Bild uppladdad");
    } catch {
      toast.error("Uppladdningen misslyckades");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Multi-image upload for the post gallery (in addition to hero `image`).
  const [galleryUploading, setGalleryUploading] = useState(false);
  const onGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setGalleryUploading(true);
    const uploaded = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(r.data.url);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
      toast.success(
        uploaded.length === 1
          ? "Bild tillagd"
          : `${uploaded.length} bilder tillagda`,
      );
    } catch {
      toast.error("Uppladdningen misslyckades");
    } finally {
      setGalleryUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({
      ...f,
      images: (f.images || []).filter((_, i) => i !== idx),
    }));
  };

  const moveGalleryImage = (idx, dir) => {
    setForm((f) => {
      const arr = [...(f.images || [])];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return f;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...f, images: arr };
    });
  };


  const save = async (statusOverride) => {
    if (!form.title.trim()) {
      toast.error("Ange en rubrik");
      return;
    }
    if (!form.content.trim()) {
      toast.error("Skriv något innehåll");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      status: statusOverride || form.status,
    };
    const isPublishing = (statusOverride || form.status) === "published";
    try {
      let res;
      if (isNew) {
        res = await api.post("/admin/blog", payload);
        toast.success(
          isPublishing ? "Inlägget är publicerat" : "Utkast sparat"
        );
        setCelebration({
          variant: isPublishing ? "publish" : "draft",
          title: isPublishing
            ? "🎉 Inlägget är publicerat!"
            : "✅ Utkast sparat",
          text: isPublishing
            ? "Inlägget syns nu på sajten under /blogg."
            : "Du kan fortsätta redigera när du vill.",
          slug: res.data.slug,
        });
        navigate(`/admin/blogg/${res.data.id}`, { replace: true });
      } else {
        res = await api.put(`/admin/blog/${id}`, payload);
        setForm((f) => ({ ...f, ...res.data }));
        toast.success(
          isPublishing ? "Inlägget är publicerat" : "Ändringar sparade"
        );
        setCelebration({
          variant: isPublishing ? "publish" : "draft",
          title: isPublishing
            ? "🎉 Inlägget är publicerat!"
            : "✅ Ändringar sparade",
          text: isPublishing
            ? "Inlägget syns nu på sajten under /blogg."
            : "Senast sparat just nu.",
          slug: res.data.slug,
        });
      }
      // Auto-clear after 6s so it doesn't clutter the form forever
      setTimeout(() => setCelebration(null), 6000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Ta bort inlägget?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Borttaget");
      navigate("/admin/blogg", { replace: true });
    } catch {
      toast.error("Kunde inte ta bort inlägget");
    }
  };

  if (loading)
    return (
      <div className="text-[#5A6258] p-4 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar inlägg…
      </div>
    );

  return (
    <div data-testid="admin-blog-edit-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link
            to="/admin/blogg"
            className="text-sm text-[#5A6258] hover:text-[#D95D2E] font-semibold inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Alla inlägg
          </Link>
          <h1 className="font-heading text-3xl font-extrabold text-[#1B2A1E] mt-2">
            {isNew ? "Nytt inlägg" : form.title || "Redigera inlägg"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && form.status === "published" && (
            <Link
              to={`/blogg/${form.slug}`}
              target="_blank"
              className="inline-flex items-center gap-1.5 bg-white border border-[#E2E0D8] hover:border-[#D95D2E] text-[#1B2A1E] text-sm font-semibold px-3 py-2 rounded-md"
            >
              <Eye className="w-3.5 h-3.5" /> Visa publikt
            </Link>
          )}
          <button
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="admin-blog-save-draft"
            className="inline-flex items-center gap-2 bg-white border border-[#E2E0D8] hover:border-[#D95D2E] text-[#1B2A1E] font-semibold px-3 py-2.5 rounded-lg text-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> Spara utkast
          </button>
          <button
            onClick={() => save("published")}
            disabled={saving}
            data-testid="admin-blog-publish"
            className="inline-flex items-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Publicera
          </button>
        </div>
      </div>

      {celebration && (
        <div
          data-testid="admin-blog-celebration"
          className={`mb-5 flex items-start gap-3 rounded-xl border px-4 py-3.5 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300 ${
            celebration.variant === "publish"
              ? "bg-[#FFF1E8] border-[#F2A57C] text-[#7A2E10]"
              : "bg-[#E8F0E8] border-[#A8C5A8] text-[#1B2A1E]"
          }`}
          role="status"
          aria-live="polite"
        >
          <span
            className={`mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${
              celebration.variant === "publish"
                ? "bg-[#D95D2E] text-white"
                : "bg-[#1B2A1E] text-[#F2A57C]"
            }`}
          >
            {celebration.variant === "publish" ? (
              <PartyPopper className="w-5 h-5" />
            ) : (
              <CheckCircle2 className="w-5 h-5" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-base">
              {celebration.title}
            </div>
            <div className="text-sm opacity-90 mt-0.5">{celebration.text}</div>
          </div>
          {celebration.variant === "publish" && celebration.slug && (
            <Link
              to={`/blogg/${celebration.slug}`}
              target="_blank"
              data-testid="admin-blog-celebration-view-link"
              className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-[#D95D2E] text-[#D95D2E] hover:bg-[#D95D2E] hover:text-white font-semibold px-3 py-2 rounded-md text-sm transition-colors"
            >
              <Eye className="w-3.5 h-3.5" /> Visa publikt
            </Link>
          )}
          <button
            type="button"
            onClick={() => setCelebration(null)}
            aria-label="Stäng meddelande"
            className="shrink-0 text-current opacity-50 hover:opacity-100 transition-opacity p-1"
          >
            <Trash2 className="w-3.5 h-3.5" style={{ display: "none" }} />
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-5">
          <Card>
            <Label>Rubrik *</Label>
            <input
              data-testid="admin-blog-title"
              value={form.title}
              onChange={setField("title")}
              placeholder="Ex: Stubbfräsning i Båstad idag"
              className="input"
            />
          </Card>
          <Card>
            <Label>Kort sammanfattning</Label>
            <textarea
              data-testid="admin-blog-excerpt"
              value={form.excerpt}
              onChange={setField("excerpt")}
              rows={2}
              placeholder="En kort sammanfattning som visas i listor och i sökresultat."
              className="textarea"
            />
          </Card>
          <Card>
            <Label>Innehåll *</Label>
            <textarea
              data-testid="admin-blog-content"
              value={form.content}
              onChange={setField("content")}
              rows={14}
              placeholder="Skriv ditt blogginlägg. Dubbla radbryt skapar nytt stycke."
              className="textarea"
            />
          </Card>
          <Card>
            <Label>Videolänk (valfritt)</Label>
            <input
              data-testid="admin-blog-video"
              value={form.video_url}
              onChange={setField("video_url")}
              placeholder="YouTube-, Facebook- eller Instagram-länk"
              className="input"
            />
            <p className="text-xs text-[#5A6258] mt-2">
              YouTube och Vimeo bäddas in direkt. Facebook-videor försöker bäddas in (publika videor) med fallback-knapp. Instagram, TikTok m.fl. visas som &quot;Öppna video&quot;-knapp.
            </p>
          </Card>
        </div>

        <div className="lg:col-span-4 space-y-5">
          <Card>
            <Label>Bild</Label>
            {form.image ? (
              <div className="relative rounded-lg overflow-hidden border border-[#E2E0D8] aspect-[16/10] mb-3">
                <img
                  src={form.image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-[#E2E0D8] aspect-[16/10] flex items-center justify-center mb-3 text-[#5A6258] text-sm">
                <ImageIcon className="w-5 h-5 mr-2" /> Ingen bild
              </div>
            )}
            <label className="block">
              <span className="inline-flex items-center gap-2 cursor-pointer bg-[#1B2A1E] hover:bg-[#283224] text-white text-sm font-semibold px-3 py-2 rounded-md">
                {uploading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ImageIcon className="w-3.5 h-3.5" />
                )}
                {uploading ? "Laddar upp…" : "Ladda upp bild"}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={onUpload}
                data-testid="admin-blog-upload"
                className="sr-only"
              />
            </label>
            {form.image && (
              <button
                onClick={() => setField("image")("")}
                className="ml-2 text-xs text-[#5A6258] hover:text-red-600 font-semibold"
              >
                Ta bort bild
              </button>
            )}
          </Card>

          <Card>
            <Label>Bildgalleri (valfritt)</Label>
            <p className="text-xs text-[#5A6258] mb-3">
              Visas under innehållet på inlägget. Klicka för att ladda upp flera
              bilder, ordna med pilarna eller ta bort med X.
            </p>
            {form.images && form.images.length > 0 && (
              <ul
                data-testid="admin-blog-gallery-list"
                className="grid grid-cols-3 gap-2 mb-3"
              >
                {form.images.map((src, idx) => (
                  <li
                    key={`${src}-${idx}`}
                    data-testid={`admin-blog-gallery-item-${idx}`}
                    className="relative group rounded-md overflow-hidden border border-[#E2E0D8] aspect-square bg-[#F7F5F0]"
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 p-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => moveGalleryImage(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Flytta vänster"
                        data-testid={`admin-blog-gallery-move-left-${idx}`}
                        className="bg-white/95 disabled:opacity-40 text-[#1B2A1E] rounded-sm w-6 h-6 text-xs font-bold flex items-center justify-center"
                      >
                        ◀
                      </button>
                      <button
                        type="button"
                        onClick={() => moveGalleryImage(idx, 1)}
                        disabled={idx === form.images.length - 1}
                        aria-label="Flytta höger"
                        data-testid={`admin-blog-gallery-move-right-${idx}`}
                        className="bg-white/95 disabled:opacity-40 text-[#1B2A1E] rounded-sm w-6 h-6 text-xs font-bold flex items-center justify-center"
                      >
                        ▶
                      </button>
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(idx)}
                        aria-label="Ta bort bild"
                        data-testid={`admin-blog-gallery-remove-${idx}`}
                        className="bg-white/95 text-red-600 rounded-sm w-6 h-6 text-xs font-bold flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <label className="block">
              <span className="inline-flex items-center gap-2 cursor-pointer bg-white border border-[#E2E0D8] hover:border-[#D95D2E] text-[#1B2A1E] text-sm font-semibold px-3 py-2 rounded-md">
                {galleryUploading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ImageIcon className="w-3.5 h-3.5" />
                )}
                {galleryUploading
                  ? "Laddar upp…"
                  : "Lägg till bilder (flera)"}
              </span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={onGalleryUpload}
                data-testid="admin-blog-gallery-upload"
                className="sr-only"
              />
            </label>
          </Card>

          <Card>
            <Label>URL (slug)</Label>
            <input
              data-testid="admin-blog-slug"
              value={form.slug || ""}
              onChange={setField("slug")}
              placeholder="genereras automatiskt från rubriken"
              className="input"
            />
          </Card>

          <Card>
            <Label>Meta-titel (SEO)</Label>
            <input
              data-testid="admin-blog-meta-title"
              value={form.meta_title || ""}
              onChange={setField("meta_title")}
              placeholder="(valfritt — annars används rubriken)"
              className="input"
            />
            <Label className="mt-3">Meta-beskrivning (SEO)</Label>
            <textarea
              data-testid="admin-blog-meta-desc"
              value={form.meta_description || ""}
              onChange={setField("meta_description")}
              rows={3}
              placeholder="(valfritt — annars används sammanfattningen)"
              className="textarea"
            />
          </Card>

          {!isNew && (
            <button
              onClick={remove}
              data-testid="admin-blog-delete"
              className="w-full inline-flex items-center justify-center gap-2 bg-white border border-[#E2E0D8] hover:border-red-500 hover:text-red-600 text-[#3a4338] font-semibold px-3 py-2.5 rounded-lg text-sm"
            >
              <Trash2 className="w-4 h-4" /> Ta bort inlägg
            </button>
          )}
        </div>
      </div>

      <style>{`
        .input { width:100%; background:#fff; border:1px solid #E2E0D8; border-radius:8px; padding:10px 14px; height:44px; color:#18221A; }
        .input:focus, .textarea:focus { outline:none; box-shadow: 0 0 0 2px #D95D2E; border-color:transparent; }
        .textarea { width:100%; background:#fff; border:1px solid #E2E0D8; border-radius:8px; padding:12px 14px; color:#18221A; resize:vertical; line-height:1.55; }
      `}</style>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white border border-[#E2E0D8] rounded-xl p-5">
      {children}
    </div>
  );
}

function Label({ children, className = "" }) {
  return (
    <div
      className={`text-xs uppercase tracking-[0.14em] font-bold text-[#5A6258] mb-1.5 ${className}`}
    >
      {children}
    </div>
  );
}
